import { useCallback } from 'react';

import {
  isBusinessError,
  isApiError,
  LOADING,
  makeApiLoaded,
  makeApiReloading,
  makeApiError,
} from 'scenes/common/helpers/apiData';
import { useFunnelState } from 'scenes/common/contexts/quoteFlowContext';
import { sessionApi } from 'scenes/common/api';

import {
  LandlordFunnelState,
  LandlordFormState,
  LandlordStepType,
  LandlordFormAnswer,
  LandlordApiResponses,
  ReferenceData,
} from 'scenes/landlord/types';
import { quoteApi } from 'scenes/landlord/api';
import {
  setFlybuysNumber,
  setHasFlybuys,
  setPolicyStartDate,
  setPromoCode,
  resetPropertyBuiltYear,
} from 'scenes/landlord/actions';
import { getQuoteNumber, getQuoteReference } from 'scenes/landlord/selectors';
import analytics from 'scenes/landlord/analytics';

export const useQuoteActions = (
  handleAnswer: (f: LandlordFormAnswer) => void,
  onNextIfLocationIs: (s: LandlordStepType, values?: LandlordFormState) => void,
  onApiResponse: (data: Partial<LandlordApiResponses>) => void,
  referenceData: ReferenceData
) => {
  const funnelState = useFunnelState<LandlordFunnelState>();
  const handleQuoteResponse = useCallback(
    (quoteDetails) => {
      onApiResponse({
        quoteDetails,
      });
      return quoteDetails;
    },
    [onApiResponse]
  );

  const onCreateQuote = useCallback(() => {
    onApiResponse({
      quoteDetails: LOADING,
    });
    quoteApi()
      .createQuote(funnelState, referenceData)
      .then(handleQuoteResponse)
      .then((quoteDetails) => {
        if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
          onNextIfLocationIs(funnelState.step);
        }
      });
  }, [
    funnelState,
    handleQuoteResponse,
    onApiResponse,
    onNextIfLocationIs,
    referenceData,
  ]);

  const onUpdateQuoteDetails = useCallback(() => {
    onApiResponse({
      quoteDetails: LOADING,
    });
    quoteApi()
      .updateQuoteDetails(funnelState, referenceData)
      .then(handleQuoteResponse)
      .then((quoteDetails) => {
        if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
          onNextIfLocationIs(funnelState.step);
        }
      });
  }, [
    onApiResponse,
    funnelState,
    referenceData,
    handleQuoteResponse,
    onNextIfLocationIs,
  ]);

  const onUpdatePolicyStartDate = (policyStartDate: string) => {
    onApiResponse({
      quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
    });
    quoteApi()
      .updatePolicyStartDate(funnelState, policyStartDate, referenceData)
      .then(handleQuoteResponse)
      .then((quoteDetails) => {
        if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
          handleAnswer(setPolicyStartDate(policyStartDate));
          onNextIfLocationIs(funnelState.step);
        }
      });
  };

  const onSaveQuote = useCallback(
    async (emailAddress: string) => {
      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });

      try {
        const saveQuoteResponse = await quoteApi().updateToSaveQuote(
          funnelState,
          emailAddress,
          referenceData
        );

        if (!isApiError(saveQuoteResponse)) {
          const emailQuoteResponse = await quoteApi().updateToEmailQuote(
            funnelState,
            emailAddress,
            referenceData
          );

          if (!isApiError(emailQuoteResponse)) {
            await sessionApi().unlockSession(
              getQuoteNumber(funnelState),
              getQuoteReference(funnelState)
            );

            analytics().emailQuote(funnelState);
            handleQuoteResponse(emailQuoteResponse);
          }
        }
      } catch (err) {
        // TODO - analytics
        // analytics().error(
        //   funnelState,
        //   `Session api: ${err.message}`,
        //   isBusinessError(err) ? ApiState.BusinessError : ApiState.ApiError
        // );

        onApiResponse({
          quoteDetails: makeApiError(
            err.message,
            funnelState.apiResponses.quoteDetails
          ),
        });
      }
    },
    [funnelState, handleQuoteResponse, onApiResponse, referenceData]
  );

  const onResumeQuote = useCallback(async () => {
    onApiResponse({
      quoteDetails: makeApiLoaded(funnelState.apiResponses.quoteDetails),
    });
  }, [onApiResponse, funnelState]);

  const onUpdatePromoCode = useCallback(
    (promoCode: string) => {
      handleAnswer(setPromoCode(promoCode));
    },
    [handleAnswer]
  );

  const onUpdateFlybuys = useCallback(
    (hasFlybuys: string, flybuysNumber?: string) =>
      flybuysNumber === undefined
        ? handleAnswer(setHasFlybuys(hasFlybuys))
        : handleAnswer(setFlybuysNumber(flybuysNumber)),
    [handleAnswer]
  );

  const onResetPropertyBuiltYear = useCallback(
    () => handleAnswer(resetPropertyBuiltYear()),
    [handleAnswer]
  );

  return {
    onResumeQuote,
    onSaveQuote,
    onCreateQuote,
    onUpdateQuoteDetails,
    onUpdatePromoCode,
    onUpdatePolicyStartDate,
    onUpdateFlybuys,
    onResetPropertyBuiltYear,
  };
};

export default useQuoteActions;
